import { useState } from "react";
import { useParams } from "wouter";
import { useAssets } from "@/contexts/AssetContext.jsx";
import { findAssetByRouteId } from "@/lib/asset-data-utils.js";
import { formatNumber } from "@/lib/formatting-utils.js";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import { Button } from "@/components/ui/button.jsx";
import { Gauge, AlertTriangle, Monitor, List } from "lucide-react";
import AssetMetadataSummary from "@/components/asset-metadata-summary.jsx";
import AssetLocationPanel from "@/components/asset-location-panel.jsx";
import AssetKpiSummary from "@/components/asset-kpi-summary.jsx";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion.jsx";
import AssetLayout from "./asset-layout.jsx";
import GaugeComponent from "@/components/ui/gauge.jsx";
import HistoryTrendPopup from "@/components/history-trend-popup.jsx";
import { getStatusColorClasses } from "@/lib/status-utils.js";
import { useTagData } from "@/hooks/useTagData.js";
import { arrayUtils, cn } from "@/lib/utils.js";
import { cardStyles, segmentedTabIdle, segmentedTabSelected, segmentedTabTrack } from "@/lib/status-styles.js";
import ParameterStatusBadge from "@/components/parameter-status-badge.jsx";

/**
 * Asset Overview page component
 * Displays asset information, tag parameters, and related data
 * @returns {JSX.Element} Asset Overview page
 */
export default function AssetOverview() {
  const { id } = useParams();
  const { assets: compressors } = useAssets();
  const compressor = findAssetByRouteId(compressors, id);

  const [viewMode, setViewMode] = useState("list");
  const [isTrendPopupOpen, setIsTrendPopupOpen] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState(null);

  const {
    tagData,
    isLoading: isTagDataLoading,
    error: tagDataError,
  } = useTagData(compressor?.id, !!compressor);

  const handleGaugeClick = (parameter) => {
    setSelectedParameter(parameter);
    setIsTrendPopupOpen(true);
  };

  const closeTrendPopup = () => {
    setIsTrendPopupOpen(false);
    setSelectedParameter(null);
  };

  const displayTagData = tagData || {};

  if (!compressor) {
    return null;
  }

  return (
    <AssetLayout>
      <div className='space-y-6 sm:space-y-8'>
        <Card className='bg-card border border-border shadow-sm rounded-lg overflow-hidden'>
          <CardHeader>
            <CardTitle>Asset Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='grid grid-cols-1 md:grid-cols-5 gap-6'>
              <div className='md:col-span-3 overflow-x-auto'>
                <AssetMetadataSummary asset={compressor} />
              </div>
              <div className='md:col-span-2'>
                <AssetLocationPanel
                  latitude={compressor.latitude}
                  longitude={compressor.longitude}
                  status={compressor.status}
                  assetNumber={compressor.assetNumber}
                />
              </div>
            </div>
            <AssetKpiSummary compressor={compressor} tagData={tagData} />
          </CardContent>
        </Card>

        <Card className='bg-card border border-border shadow-sm rounded-lg overflow-hidden'>
          <CardHeader>
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
              <div>
                <CardTitle>Operational Parameters</CardTitle>
              </div>
              <div className={segmentedTabTrack}>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => setViewMode("list")}
                  data-testid='button-list-view'
                  className={cn(
                    "h-8 shadow-none hover:bg-transparent",
                    viewMode === "list"
                      ? segmentedTabSelected
                      : segmentedTabIdle
                  )}>
                  <List className='h-4 w-4 mr-2' />
                  List View
                </Button>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => setViewMode("gauge")}
                  data-testid='button-gauge-view'
                  className={cn(
                    "h-8 shadow-none hover:bg-transparent",
                    viewMode === "gauge"
                      ? segmentedTabSelected
                      : segmentedTabIdle
                  )}>
                  <Gauge className='h-4 w-4 mr-2' />
                  Gauge View
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isTagDataLoading ? (
              <div className='flex items-center justify-center py-8'>
                <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
                <span className='ml-3 text-muted-foreground'>
                  Loading tag data...
                </span>
              </div>
            ) : tagDataError ? (
              <div className='flex items-center justify-center py-8'>
                <div className='text-center'>
                  <AlertTriangle className='h-8 w-8 text-red-500 mx-auto mb-2' />
                  <p className='text-red-600 mb-2'>{tagDataError}</p>
                  <p className='text-sm text-muted-foreground'>
                    Please try refreshing the page
                  </p>
                </div>
              </div>
            ) : arrayUtils.isEmpty(Object.keys(displayTagData)) ? (
              <div className='flex items-center justify-center py-8'>
                <div className='text-center'>
                  <Monitor className='h-8 w-8 text-muted-foreground mx-auto mb-2' />
                  <p className='text-muted-foreground mb-2'>
                    No operational data available
                  </p>
                  <p className='text-sm text-muted-foreground'>
                    This asset may not have any operational parameters
                    configured
                  </p>
                </div>
              </div>
            ) : (
              <Accordion type='multiple' className='w-full'>
                {Object.entries(displayTagData)
                  .filter(([categoryName, parameters]) =>
                    arrayUtils.hasItems(parameters)
                  )
                  .map(([categoryName, parameters]) => (
                    <AccordionItem
                      key={categoryName}
                      value={categoryName.toLowerCase()}
                      className='border-border'>
                      <AccordionTrigger
                        data-testid={`accordion-${categoryName.toLowerCase()}`}
                        className='text-muted-foreground hover:text-foreground hover:bg-muted/50 [&[data-state=open]]:text-muted-foreground'>
                        {categoryName} ({parameters.length})
                      </AccordionTrigger>
                      <AccordionContent className='border-l-0 bg-transparent'>
                        {viewMode === "list" ? (
                          <div className='overflow-x-auto'>
                            <table className='w-full border-collapse border border-border'>
                              <thead>
                                <tr className='bg-muted'>
                                  <th className='border border-border px-4 py-2 text-left text-sm font-medium text-muted-foreground'>
                                    Parameter
                                  </th>
                                  <th className='border border-border px-4 py-2 text-left text-sm font-medium text-muted-foreground'>
                                    Current Value
                                  </th>
                                  <th className='border border-border px-4 py-2 text-left text-sm font-medium text-muted-foreground'>
                                    Unit
                                  </th>
                                  <th className='border border-border px-4 py-2 text-center text-sm font-medium text-muted-foreground'>
                                    LOLO
                                  </th>
                                  <th className='border border-border px-4 py-2 text-center text-sm font-medium text-muted-foreground'>
                                    LO
                                  </th>
                                  <th className='border border-border px-4 py-2 text-center text-sm font-medium text-muted-foreground'>
                                    HI
                                  </th>
                                  <th className='border border-border px-4 py-2 text-center text-sm font-medium text-muted-foreground'>
                                    HIHI
                                  </th>
                                  <th className='border border-border px-4 py-2 text-left text-sm font-medium text-muted-foreground'>
                                    Status
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {parameters.map((param, index) => (
                                  <tr
                                    key={index}
                                    className={cn(
                                      cardStyles.bodyRow,
                                      "cursor-pointer"
                                    )}
                                    onClick={() => handleGaugeClick(param)}
                                    data-testid={`row-${categoryName.toLowerCase()}-${index}`}>
                                    <td className='border border-border px-4 py-2 text-sm font-medium text-foreground'>
                                      {param.parameter}
                                    </td>
                                    <td className='border border-border px-4 py-2 text-sm text-foreground'>
                                      {param.displayType === "Text" ? (
                                        <div
                                          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getStatusColorClasses(param.status)}`}
                                          title={`Status: ${param.status}`}>
                                          {param.currentValue}
                                        </div>
                                      ) : (
                                        formatNumber(param.currentValue, 2)
                                      )}
                                    </td>
                                    <td className='border border-border px-4 py-2 text-center text-sm text-foreground'>
                                      {param.unit || "-"}
                                    </td>
                                    <td className='border border-border px-4 py-2 text-center text-sm text-foreground'>
                                      {param.displayType === "Gauge"
                                        ? formatNumber(
                                            param.alarmSetPoints.lolo,
                                            2
                                          )
                                        : "-"}
                                    </td>
                                    <td className='border border-border px-4 py-2 text-center text-sm text-foreground'>
                                      {param.displayType === "Gauge"
                                        ? formatNumber(
                                            param.alarmSetPoints.lo,
                                            2
                                          )
                                        : "-"}
                                    </td>
                                    <td className='border border-border px-4 py-2 text-center text-sm text-foreground'>
                                      {param.displayType === "Gauge"
                                        ? formatNumber(
                                            param.alarmSetPoints.hi,
                                            2
                                          )
                                        : "-"}
                                    </td>
                                    <td className='border border-border px-4 py-2 text-center text-sm text-foreground'>
                                      {param.displayType === "Gauge"
                                        ? formatNumber(
                                            param.alarmSetPoints.hihi,
                                            2
                                          )
                                        : "-"}
                                    </td>
                                    <td className='border border-border px-4 py-2'>
                                      <ParameterStatusBadge param={param} />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                            {parameters.map((param, index) => (
                              <div
                                key={index}
                                className='bg-card border border-border rounded-lg p-4 cursor-pointer hover:bg-muted hover:border-border transition-colors'
                                onClick={() => handleGaugeClick(param)}
                                data-testid={`gauge-${categoryName.toLowerCase()}-${index}`}>
                                <div className='text-center mb-4'>
                                  <h4 className='font-medium text-foreground'>
                                    {param.parameter}
                                  </h4>
                                  <p className='text-sm text-muted-foreground'>
                                    {param.unit}
                                  </p>
                                </div>
                                {param.displayType === "Text" ? (
                                  <div className='flex items-center justify-center mb-4'>
                                    <div
                                      className={`inline-flex items-center rounded-full border px-4 py-2 text-lg font-medium ${getStatusColorClasses(param.status)}`}
                                      title={`Status: ${param.status}`}>
                                      {param.currentValue}
                                    </div>
                                  </div>
                                ) : (
                                  <GaugeComponent param={param} index={index} />
                                )}
                                {param.displayType === "Gauge" && (
                                  <div className='text-xs text-muted-foreground mb-2'>
                                    <div className='grid grid-cols-4 gap-1 text-center'>
                                      <div className='text-red-600'>
                                        <div className='font-semibold'>
                                          LOLO
                                        </div>
                                        <div>
                                          {formatNumber(
                                            param.alarmSetPoints.lolo,
                                            2
                                          )}
                                        </div>
                                      </div>
                                      <div className='text-orange-600'>
                                        <div className='font-semibold'>LO</div>
                                        <div>
                                          {formatNumber(
                                            param.alarmSetPoints.lo,
                                            2
                                          )}
                                        </div>
                                      </div>
                                      <div className='text-orange-600'>
                                        <div className='font-semibold'>HI</div>
                                        <div>
                                          {formatNumber(
                                            param.alarmSetPoints.hi,
                                            2
                                          )}
                                        </div>
                                      </div>
                                      <div className='text-red-600'>
                                        <div className='font-semibold'>
                                          HIHI
                                        </div>
                                        <div>
                                          {formatNumber(
                                            param.alarmSetPoints.hihi,
                                            2
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                <div className='text-center'>
                                  {(() => {
                                    const currentValue = param.currentValue;
                                    const { lolo, lo, hi, hihi } =
                                      param.alarmSetPoints;

                                    if (
                                      currentValue >= hihi
                                      || currentValue <= lolo
                                    ) {
                                      return (
                                        <Badge
                                          variant='destructive'
                                          className='bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/30'>
                                          Danger
                                        </Badge>
                                      );
                                    }

                                    if (
                                      currentValue >= hi
                                      || currentValue <= lo
                                    ) {
                                      return (
                                        <Badge
                                          variant='outline'
                                          className='bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30'>
                                          Alerted
                                        </Badge>
                                      );
                                    }

                                    if (param.status === "NoData") {
                                      return (
                                        <Badge
                                          variant='secondary'
                                          className='bg-muted text-muted-foreground border-border'>
                                          No Data
                                        </Badge>
                                      );
                                    }

                                    return (
                                      <Badge
                                        variant='secondary'
                                        className='bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30'>
                                        Normal
                                      </Badge>
                                    );
                                  })()}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
              </Accordion>
            )}
          </CardContent>
        </Card>
      </div>

      <HistoryTrendPopup
        isOpen={isTrendPopupOpen}
        onClose={closeTrendPopup}
        parameterData={selectedParameter}
        assetNumber={compressor?.assetNumber || id}
        location={compressor?.location || "Unknown Location"}
      />
    </AssetLayout>
  );
}
